





















import { defineComponent, ref, computed, PropType } from "@vue/composition-api";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString } from "@/lib/moneyHelpers";
import { format, parse } from "@/lib/time";
import { FormFields, FormValues, PostFileData } from "@/types";
import BaseForm from "@/components/BaseForm";
import EditPostDropZone from "../EditPostDropZone";
import PostFilesArea from "../PostFilesArea";

export default defineComponent({
  name: "EventPostEditView",
  components: { BaseForm, PostFilesArea, EditPostDropZone },
  props: {
    postData: { type: Object as PropType<ContentSet>, required: true },
    fileList: { type: Array as PropType<PostFileData[]>, required: true },
  },
  setup(props, { emit }) {
    const priceList = ref<SubscriptionLevel[]>([]);
    const packageList = ref<PaymentPackage[]>([]);
    const pricePackageId = ref("0");
    const levelId = ref("0");
    const values = ref<FormValues>({
      date: format(new Date(), "yyyy-MM-dd"),
      time: format(new Date(), "HH:mm"),
      duration: 15,
      levelId: levelId.value,
      pricePackageId: pricePackageId.value,
    });

    const fields = computed<FormFields>(() => {
      const packageOptions = [{ text: $t("Disable one-time purchase"), value: "0" }].concat(
        packageList.value.map(packageData => ({
          text: formatMoneyString(packageData.amount),
          value: packageData.id,
        }))
      );

      return [
        {
          name: "date",
          label: $t("Date"),
          type: "date",
          rules: ["required"],
          disabled: props.postData.published,
          validationMode: "aggressive",
        },
        {
          name: "time",
          label: $t("Time"),
          type: "time",
          rules: ["required"],
          disabled: props.postData.published,
          validationMode: "aggressive",
        },
        {
          name: "duration",
          label: $t("Duration"),
          type: "select",
          rules: ["required"],
          disabled: props.postData.published,
          validationMode: "aggressive",
          options: [
            { text: "15 min", value: 15 },
            { text: "30 min", value: 30 },
            { text: "60 min", value: 60 },
            { text: "90 min", value: 90 },
            { text: "120 min", value: 120 },
          ],
        },
        {
          name: "pricePackageId",
          label: $t("Ticket price"),
          type: "select",
          rules: ["required"],
          disabled: props.postData.published,
          validationMode: "aggressive",
          options: packageOptions,
        },
      ];
    });

    const sendEventData = async () => {
      if (props.postData.published) return;
      await providers.EventPostProvider.update({
        data: {
          postId: props.postData.id,
          time: parse(`${values.value.date} ${values.value.time}`, "yyyy-MM-dd HH:mm", new Date()).toISOString(),
          duration: values.value.duration,
        },
      });
      await Promise.all([
        providers.ContentPriceProvider.update({
          data: [
            {
              subject: {
                subjectId: props.postData.id,
                subjectType: ContentFeedTypeEnum.EVENT,
              },
              packageId: values.value.pricePackageId,
            },
          ],
        }),
        providers.ContentSubscriptionLevelProvider.update({
          data: {
            subject: {
              subjectId: props.postData.id,
              subjectType: ContentFeedTypeEnum.EVENT,
            },
            levelId: values.value.levelId,
          },
        }),
      ]);
    };

    const getData = async () => {
      priceList.value = await providers.SubscriptionLevelProvider.find({ userIds: [props.postData.userId] });
      packageList.value = await providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.FILESET] });
      const data = await providers.EventPostProvider.find({ postIds: [props.postData.id] });
      values.value.date = format(
        new Date((data.find(i => i.postId === props.postData.id) || { time: new Date() }).time),
        "yyyy-MM-dd"
      );
      values.value.time = format(
        new Date((data.find(i => i.postId === props.postData.id) || { time: new Date() }).time),
        "HH:mm"
      );
      values.value.duration = (data.find(i => i.postId === props.postData.id) || { duration: 15 }).duration;
      values.value.levelId = (
        data.find(i => i.postId === props.postData.id) || { minimumLevelId: levelId.value }
      ).minimumLevelId;
      values.value.pricePackageId = (
        data.find(i => i.postId === props.postData.id) || { ticketPricePackageId: pricePackageId.value }
      ).ticketPricePackageId;
    };
    getData();
    emit("setUpdateCb", () => sendEventData());

    return {
      values,
      fields,
    };
  },
});
